import {
    resolvedPromiseLike,
    type NamedService,
    type ServiceDescriptor,
} from '@conduit-client/utils';
import type { RetryService } from '@conduit-client/service-retry/v1';

export type FetchParameters = Parameters<typeof fetch>;
/**
 * The first argument to `fetch` (a `RequestInfo | URL` in lib.dom). Pulled
 * via index access so we don't depend on the DOM `RequestInfo` name being
 * directly resolvable in every consuming tsconfig.
 */
export type FetchInput = FetchParameters[0];
/**
 * Optional per-call data passed by callers (typically `FetchNetworkCommand`
 * subclasses) and merged into the per-request interceptor context inside
 * `buildServiceDescriptor`. Carried on the `init` argument under the
 * `__contextSeed` key (see `ExtendedRequestInit`) so that detection is a
 * named-property lookup rather than positional arity sniffing — runtime
 * type checks beat length checks for distinguishing a seed from a vanilla
 * `RequestInit`. The seed is stripped from `init` before the underlying
 * `fetch` call, so it never reaches the network.
 */
export type FetchContextSeed = Record<string, unknown>;
/**
 * `RequestInit` plus an optional `__contextSeed` slot. Every existing
 * `RequestInit` value is assignable to this type, so today's callers
 * continue to compile and behave identically.
 */
export type ExtendedRequestInit = RequestInit & { __contextSeed?: FetchContextSeed };
export type FetchService = (input: FetchInput, init?: ExtendedRequestInit) => PromiseLike<Response>;
export type IFetchService = FetchService;
export type FetchServiceDescriptor = ServiceDescriptor<IFetchService, 'fetch', '1.0'>;
export type NamedFetchService<Name extends string = 'fetch'> = NamedService<Name, IFetchService>;
export type RequestInterceptor<Context = unknown> = (
    fetchArgs: FetchParameters,
    context?: Context
) => PromiseLike<FetchParameters>;
export type RetryInterceptor<Context = unknown> = (
    fetchArgs: FetchParameters,
    retryService?: RetryService<Response>,
    context?: Context
) => PromiseLike<Response>;
export type ResponseInterceptor<Context = unknown> = (
    response: Response,
    context?: Context
) => PromiseLike<Response>;
export type FinallyInterceptor<Context = unknown> = (context?: Context) => PromiseLike<void> | void;
export type Interceptors<Context = unknown> = {
    createContext?: () => Context;
    request?: RequestInterceptor<Context>[];
    retry?: RetryInterceptor<Context>;
    response?: ResponseInterceptor<Context>[];
    finally?: FinallyInterceptor<Context>[];
};
export function buildServiceDescriptor<Context = unknown>(
    interceptors: Interceptors<Context> = {
        request: [],
        retry: undefined,
        response: [],
        finally: [],
    },
    retryService?: RetryService<Response>
): FetchServiceDescriptor {
    return {
        type: 'fetch',
        version: '1.0',
        service: function (input: FetchInput, init?: ExtendedRequestInit) {
            // Detect the optional context seed via a named property on init.
            // Strip it before forwarding so the underlying fetch call shape
            // matches today's exactly (no extra arg, no extra init key on
            // the wire).
            let contextSeed: FetchContextSeed | undefined;
            let cleanInit: RequestInit | undefined = init;
            if (init !== undefined && '__contextSeed' in init) {
                const { __contextSeed, ...initWithoutSeed } = init;
                contextSeed = __contextSeed;
                // Preserve today's call shape: if init only carried the seed,
                // pass `undefined` rather than `{}` so single-arg fetch calls
                // remain single-arg.
                cleanInit = Object.keys(initWithoutSeed).length === 0 ? undefined : initWithoutSeed;
            }
            const fetchArgs: FetchParameters =
                cleanInit === undefined ? [input] : [input, cleanInit];

            // Create context per request if factory is provided, then layer
            // the per-call seed on top so seeded keys win over factory defaults.
            const baseContext = interceptors.createContext?.();
            const context =
                contextSeed === undefined
                    ? baseContext
                    : ({ ...(baseContext as object), ...contextSeed } as Context);

            const {
                request: requestInterceptors = [],
                retry: retryInterceptor = undefined,
                response: responseInterceptors = [],
                finally: finallyInterceptors = [],
            } = interceptors;

            const pending = requestInterceptors.reduce(
                (previousPromise, interceptor) =>
                    previousPromise.then((args) => interceptor(args, context)),
                resolvedPromiseLike(fetchArgs)
            );

            return Promise.resolve(pending)
                .then((args) => {
                    if (retryInterceptor) {
                        return retryInterceptor(args, retryService, context);
                    } else {
                        // default retry logic
                        if (retryService) {
                            return retryService.applyRetry(() => fetch(...args));
                        }
                        return fetch(...args);
                    }
                })
                .then((response) => {
                    // Success path - run response interceptors
                    return responseInterceptors.reduce(
                        (previousPromise, interceptor) =>
                            previousPromise.then((response) => interceptor(response, context)),
                        resolvedPromiseLike(response)
                    );
                })
                .finally(() => {
                    // Always run finally interceptors for cleanup
                    if (finallyInterceptors.length > 0) {
                        // Run all finally interceptors sequentially
                        return finallyInterceptors.reduce(
                            (previousPromise, interceptor) =>
                                previousPromise.then(() => interceptor(context)),
                            Promise.resolve()
                        );
                    }
                });
        },
    };
}
